import {useIntl} from 'react-intl'
import {
  Button,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../../../gori/widgets'
import {TextEditor} from './TextEditor'
import * as Yup from 'yup'
import {useCancelToken, UseYupValidate} from '../../../../../../../gori/hooks'
import {useMemo, useState} from 'react'
import {useFormik} from 'formik'
import {A1BatchService} from '../../../../core/requests'
import {useA1BatchesProvider} from '../../../../../../../gori/providers'

type AddTicketFormProps = {
  tabName: string
  onSuccessCallback: (recipient: string) => void
  onCancel: () => void
  recipientOptions: {label: string; value: string}[]
}
export const AddTicketForm = ({
  tabName,
  onSuccessCallback,
  onCancel,
  recipientOptions,
}: AddTicketFormProps) => {
  const {stringYup} = UseYupValidate()
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {a1Batch} = useA1BatchesProvider()
  const [loading, setLoading] = useState(false)

  const [validationErrors, setValidationErrors] = useState<any>()

  const validateSchema = useMemo(
    () =>
      Yup.object({
        name: stringYup(255, 'TICKET_NAME', true),
        content: Yup.string().when('hasAddTicket', {
          is: true,
          then: stringYup('no_limit', 'TICKET_DESCRIPTION', true),
          otherwise: stringYup('no_limit', 'TICKET_DESCRIPTION', false),
        }),
        recipient: stringYup(255, 'ASSIGNEE', true),
        attachments: Yup.array(),
      }),
    [stringYup]
  )

  const formik = useFormik({
    initialValues: {name: '', content: '', attachments: [], recipient: tabName},
    validationSchema: validateSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      handleCreateTicket(values)
    },
  })

  const handleCreateTicket = async (values) => {
    try {
      setLoading(true)
      const payload = new FormData()
      payload.append('recipient', values.recipient)
      payload.append('batch_id', a1Batch?.id)
      payload.append('name', values.name)
      payload.append('content', values.content)
      formik.values.attachments.forEach((file) => payload.append('attachments[]', file))
      const res = await A1BatchService.createTicket(payload, {cancelToken: newCancelToken()})
      if (res) {
        onSuccessCallback(values.recipient)
        formik.resetForm()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelTicket = () => {
    onCancel()
    formik.resetForm()
  }
  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <div className='border-top border-gray-300 d-flex flex-column gap-8 p-10 flex-fill'>
        <div>
          <InputTextFormik
            label={intl.formatMessage({id: 'TICKET_NAME'})}
            labelClassName='fw-bold text-dark'
            formik={formik}
            name='name'
            required
          />
        </div>
        <div>
          <SelectFormik
            label={intl.formatMessage({id: 'ASSIGNEE'})}
            labelClassName='fw-bold text-dark'
            options={recipientOptions}
            required
            formik={formik}
            name='recipient'
            emptyDefault={false}
          />
        </div>
        <TextEditor formik={formik} name='content' fileName='attachments' />
      </div>
      <div className='d-flex gap-4 px-6 border-top border-gray-300 pt-5'>
        <Button
          divClassName='flex-fill'
          className='btn btn-white text-gray-700 text-hover-dark border border-gray-300 w-100 fw-bolder'
          label={intl.formatMessage({id: 'CANCEL'})}
          event={handleCancelTicket}
        />
        <Button
          divClassName='flex-fill'
          className='btn btn-primary w-100 fw-bolder'
          label={intl.formatMessage({id: 'CREATE_TICKET'})}
          event={formik.handleSubmit}
          loading={loading}
        />
      </div>
    </>
  )
}
